import { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, X, Loader2 } from "lucide-react";
import api, { mediaUrl } from "@/lib/api";

// Verkleinert/rekomprimiert das Bild im Browser, damit der Upload klein bleibt
// (umgeht Größenlimits des Produktions-Servers). Fällt bei Problemen auf das Original zurück.
async function compressImage(file) {
  if (!file || !file.type?.startsWith("image/")) return file;
  const MAX_DIM = 2200;
  try {
    let bitmap;
    try {
      bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
    } catch {
      bitmap = await createImageBitmap(file);
    }
    const scale = Math.min(1, MAX_DIM / Math.max(bitmap.width, bitmap.height));
    const w = Math.round(bitmap.width * scale);
    const h = Math.round(bitmap.height * scale);
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(bitmap, 0, 0, w, h);
    if (bitmap.close) bitmap.close();
    const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.85));
    if (!blob) return file;
    const name = (file.name || "bild").replace(/\.[^.]+$/, "") + ".jpg";
    const out = new File([blob], name, { type: "image/jpeg" });
    // Original nur behalten, wenn es bereits klein UND kleiner als das komprimierte ist
    if (file.size <= 1.5 * 1024 * 1024 && file.size < out.size) return file;
    return out;
  } catch {
    return file;
  }
}

export default function ImageUpload({ value, onChange, multiple = false, label }) {
  const inputRef = useRef();
  const [uploading, setUploading] = useState(false);
  const ids = multiple ? value || [] : value ? [value] : [];

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const f of files) {
        const prepared = await compressImage(f);
        const fd = new FormData();
        fd.append("file", prepared);
        const { data } = await api.post("/admin/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push(data.id);
      }
      if (multiple) onChange([...(value || []), ...uploaded]);
      else onChange(uploaded[0]);
      toast.success(files.length > 1 ? `${files.length} Bilder hochgeladen` : "Bild hochgeladen");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload fehlgeschlagen");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = (id) => {
    if (multiple) onChange((value || []).filter((x) => x !== id));
    else onChange(null);
  };

  return (
    <div>
      {label && <div className="text-sm font-medium mb-2">{label}</div>}
      <div className="flex flex-wrap gap-3">
        {ids.map((id) => (
          <div key={id} className="relative w-24 h-24 bg-[#F3E7D2] border border-[#E6D7EF] group">
            <img src={mediaUrl(id, true)} alt="" className="w-full h-full object-cover" />
            <button
              type="button"
              onClick={() => remove(id)}
              data-testid={`remove-image-${id}`}
              className="absolute -top-2 -right-2 bg-[#F4C542] text-[#32104F] rounded-full p-1 shadow"
              aria-label="Bild entfernen"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
        {(multiple || ids.length === 0) && (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            data-testid="image-upload-button"
            disabled={uploading}
            className="w-24 h-24 border border-dashed border-[#B79FD1] flex flex-col items-center justify-center gap-1 text-[#888] hover:border-brand hover:text-ink transition-colors bg-white"
          >
            {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" strokeWidth={1.5} />}
            <span className="text-[10px] uppercase tracking-wide">{uploading ? "Lädt…" : "Hochladen"}</span>
          </button>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif"
        multiple={multiple}
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
      />
    </div>
  );
}
